import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Globe,
  Inbox,
  Newspaper,
  Settings,
  LogOut,
  Activity,
  Sparkles,
  Layers,
  Trash2,
  Gauge,
  ShoppingCart,
} from "lucide-react";
import { SHELL, AUTH } from "@/constants/testIds";
import { useAuth } from "@/lib/AuthContext";
import eventStream from "@/lib/eventStream";

const NAV = [
  { to: "/admin", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/sites", key: "sites", label: "Sites", icon: Globe },
  { to: "/admin/domains", key: "domains", label: "Onboard Domains", icon: ShoppingCart },
  { to: "/admin/moderation", key: "moderation", label: "Moderation", icon: Newspaper, badgeKey: "moderation" },
  { to: "/admin/auto-review", key: "auto-review", label: "Auto Review", icon: Sparkles },
  { to: "/admin/inbox", key: "inbox", label: "Inbox", icon: Inbox, badgeKey: "inbox" },
  { to: "/admin/postmaster", key: "postmaster", label: "Postmaster", icon: Activity },
  { to: "/admin/template-gallery", key: "template-gallery", label: "Templates", icon: Layers },
  { to: "/admin/delete-article", key: "delete-article", label: "Delete Article", icon: Trash2 },
  { to: "/admin/capacity", key: "capacity", label: "Capacity", icon: Gauge },
  { to: "/admin/settings", key: "settings", label: "Settings", icon: Settings },
];

/** Subscribes to the SSE bus to drive Moderation + Inbox sidebar badges in real time. */
function useSidebarBadges() {
  const [badges, setBadges] = useState({ moderation: 0, inbox: 0 });
  useEffect(() => {
    const unsubMod = eventStream.subscribe("moderation_pulse", (snap) => {
      setBadges((b) => ({ ...b, moderation: snap?.actionable ?? 0 }));
    });
    const unsubInbox = eventStream.subscribe("inbox_counts", (snap) => {
      setBadges((b) => ({ ...b, inbox: snap?.total_unread ?? 0 }));
    });
    return () => { unsubMod(); unsubInbox(); };
  }, []);
  return badges;
}

function NavBadge({ value, testid, accent }) {
  if (!value || value <= 0) return null;
  const cls = accent === "amber"
    ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
    : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
  return (
    <span
      data-testid={testid}
      className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full border text-[10px] font-mono font-semibold tabular-nums ${cls}`}
    >
      {value > 99 ? "99+" : value}
    </span>
  );
}

function Sidebar() {
  const badges = useSidebarBadges();
  return (
    <aside className="w-60 shrink-0 border-r border-zinc-800 bg-zinc-950 flex flex-col">
      <div className="px-5 py-5 border-b border-zinc-800">
        <NavLink to="/admin" data-testid={SHELL.brandLink} className="block">
          <div className="font-display text-2xl font-black tracking-tight text-white leading-none">
            IL
            <span className="text-amber-500">/</span>NEWSROOM
          </div>
        </NavLink>
      </div>
      <nav className="flex-1 py-3">
        {NAV.map((n) => (
          <NavLink
            key={n.key}
            to={n.to}
            end={n.end}
            data-testid={SHELL.sidebarLink(n.key)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                isActive
                  ? "border-white text-white bg-zinc-900/60"
                  : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40"
              }`
            }
          >
            <n.icon size={16} strokeWidth={1.75} />
            <span>{n.label}</span>
            {n.badgeKey === "moderation" && (
              <NavBadge value={badges.moderation} testid="sidebar-badge-moderation" accent="amber" />
            )}
            {n.badgeKey === "inbox" && (
              <NavBadge value={badges.inbox} testid="sidebar-badge-inbox" accent="emerald" />
            )}
          </NavLink>
        ))}
      </nav>
      <Footer />
    </aside>
  );
}

function Footer() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const email = (user && typeof user === "object") ? user.email : "";
  async function handleLogout() {
    await logout();
    nav("/admin/login", { replace: true });
  }
  return (
    <div className="border-t border-zinc-800 px-5 py-4">
      <div className="label-tiny">Signed in as</div>
      <div className="text-sm text-zinc-200 mt-1 truncate font-mono">{email}</div>
      <button
        data-testid={AUTH.logoutButton}
        onClick={handleLogout}
        className="mt-3 flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
      >
        <LogOut size={14} />
        Sign out
      </button>
    </div>
  );
}

function TopBar() {
  const loc = useLocation();
  const seg = loc.pathname.split("/").filter(Boolean);
  const title = seg.length > 1 ? seg[1] : "dashboard";
  return (
    <header className="h-14 border-b border-zinc-800 bg-zinc-950/70 backdrop-blur flex items-center px-6">
      <div data-testid={SHELL.topbarTitle} className="font-display text-lg font-bold uppercase tracking-wider text-zinc-100">
        {title.replace("-", " ")}
      </div>
      <div className="ml-auto flex items-center gap-3">
        <span className="dot" style={{ background: "#10b981" }} />
        <span className="label-tiny">System Operational</span>
      </div>
    </header>
  );
}

export default function AppShell() {
  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0">
        <TopBar />
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
